import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useUserStore } from "../stores/userStore";
import apiClient from "../lib/api";

// Hook: useLogin
export const useLogin = () => {
  const login = useUserStore((state) => state.login);

  return useMutation({
    mutationKey: ["auth", "login"],
    mutationFn: async ({ email, password }) => {
      const result = await login({ email, password });
      if (!result.success) {
        throw new Error(result.error);
      }
      return result;
    },
    onSuccess: async () => {
      // No need to invalidate queries - the userStore already updates the state
      // and the UI will re-render automatically due to Zustand state changes
    },
    // Add retry configuration to prevent duplicate calls
    retry: false,
    // Add network mode to prevent duplicate calls in StrictMode
    networkMode: "online",
  });
};

// Hook: useLogout
export const useLogout = () => {
  const queryClient = useQueryClient();
  const logout = useUserStore((state) => state.logout);

  return useMutation({
    mutationKey: ["auth", "logout"],
    mutationFn: async () => {
      // The logout logic is centralized in the userStore
      const result = await logout();
      return result;
    },
    onSuccess: async () => {
      // Clear all cached queries on logout
      await queryClient.clear();
    },
    onError: () => {},
  });
};

// Hook: useAuthStatus - check if user is authenticated
export const useAuthStatus = () => {
  const isAuthenticated = useUserStore((state) => state.isAuthenticated);
  const hasValidToken = useUserStore((state) => {
    const token = apiClient.getAuthToken();
    return !!(token && state.isAuthenticated);
  });

  return {
    isAuthenticated,
    hasValidToken,
  };
};

// Hook: useForgotPassword - request password reset
export const useForgotPassword = () => {
  return useMutation({
    mutationKey: ["auth", "forgot-password"],
    mutationFn: async ({ email }) => {
      const response = await apiClient.post("/users/forgot", { email });

      if (response.error) {
        const errorMessage = Array.isArray(response.result)
          ? response.result.join(", ")
          : response.result || "Failed to send password reset email";
        throw new Error(errorMessage);
      }

      return {
        success: true,
        result: response.result,
        maxAttempts: response.max_attempts,
        currentAttempts: response.current_attempts,
        attemptsRemaining: response.max_attempts - response.current_attempts,
      };
    },
    retry: false,
    networkMode: "online",
  });
};

// Hook: useResetPassword - reset password with code and random
export const useResetPassword = () => {
  return useMutation({
    mutationKey: ["auth", "reset-password"],
    mutationFn: async ({
      code,
      random,
      password,
      "confirm-password": confirmPassword,
    }) => {
      const response = await apiClient.post("/users/reset", {
        code,
        random,
        password,
        "confirm-password": confirmPassword,
      });

      if (response.error) {
        const errorMessage = Array.isArray(response.result)
          ? response.result.join(", ")
          : response.result || "Failed to reset password";
        throw new Error(errorMessage);
      }

      return {
        success: true,
        result: response.result,
      };
    },
    retry: false,
    networkMode: "online",
  });
};

export default {
  useLogin,
  useLogout,
  useAuthStatus,
  useForgotPassword,
  useResetPassword,
};
